<template>
  <div id="app" >
    <!-- this transition is going to be visible on each page, therefore it's defined in app-->
    <!-- the transition is animated through the route.path key-->
    <transition-group mode="out-in">
      <!-- Here the components are rendered, which are passed in router/index.js of the respective route-->
      <router-view class="active-route" :page-color="pageColor" :key="$route.path" @edit-todo="handleEditTodo" @show-modal="showModal = true"/>
      <TodoTopBar v-if="hideNav" class="active-route" key="topBar" :page-color="pageColor"/>
      <TodoNav v-if="hideNav" class="active-route" key="navBar" :page-color="pageColor" @show-modal="showModal = true"/>
    </transition-group>
    <!-- the toast that are stored in the store are rendered here-->
    <transition-group class="toaster" tag="div">
      <TodoToast
        v-for="toast in $store.state.toasts"
        :key="toast.id"
        :action="toast.action"
        :action-label="toast.actionLabel"
        :message="toast.message"
        :timeout="toast.timeout"
        @close="$store.commit('removeToast', toast.id)"
      />
    </transition-group>
    <!-- the modal shall be accessible from all views and components therefore it's defined in app.vue as well.-->
    <TodoModal title="Add a rhythm" :todo="todoBeingEdited" :visible="showModal" @close="closeTodoModal" @add="addTodo" @update="updateTodo(todoBeingEdited.id, $event)">
    </TodoModal>
  </div>
</template>

<script>
import TodoNav from '@/components/TodoNav.vue';
import TodoTopBar from '@/components/TodoTopBar.vue';
import TodoToast from '@/components/TodoToast.vue';
import TodoModal from '@/components/TodoModal.vue';
import db from '@/db.js';

export default {
  components: {
    TodoNav,
    TodoTopBar,
    TodoToast,
    TodoModal,
  },
  async created() {
    // the beforeinstallprompt-event is intercepted to decide when the user can install the app
    window.addEventListener('beforeinstallprompt', (prompt) => {
      prompt.preventDefault(); // it's not displayed right when opening the app
      this.$store.commit('setInstallPrompt', prompt); // it's stored in vuex
    });

    try {
      // all settings as array
      const settings = await db.settings.toArray();
      const settingsObject = settings.reduce((accumulator, setting) => {
        accumulator[setting.name] = setting.value; // eslint-disable-line no-param-reassign
        return accumulator;
      }, {});
      // if there's the setting onboardingComplete, the property in store is changed
      if (settingsObject.onboardingComplete) this.$store.commit('setOnboardingComplete', settingsObject.onboardingComplete);
      if (settingsObject.username) this.$store.commit('setName', settingsObject.username);
    } catch (err) {
      if (err.name === 'OpenFailedError') this.$store.commit('addToast', { message: 'It looks like your browser doesn’t support saving to a database. Unfortunately, rhythm won’t work without that support. Please try a different browser, or disable private / incognito mode should you have that enabled.', timeout: -1 });
      else this.$store.commit('addToast', { message: `Sorry. Your settings couldn't be loaded.: ${err.message}` });
    }
  },
  data() {
    return {
      timecolor: 'timecolor',
      landingcolor: 'landingcolor',
      settingscolor: 'settingscolor',
      importantcolor: 'importantcolor',
      nottodocolor: 'nottodocolor',
      habitscolor: 'habitscolor',
      projectscolor: 'projectscolor',
      todoBeingEdited: null,
      activeView: null,
      showModal: false,
      showNameModal: false,
      onboardingcolor: 'onboardingcolor',
      showNav: true,
    };
  },
  computed: {
    pageColor() {
      if (this.$route.name === 'home') return this.landingcolor;
      if (this.$route.name === 'onboarding') return this.onboardingcolor;
      if (this.$route.name === 'timediagrams') return this.timecolor;
      if (this.$route.name === 'settings') return this.settingscolor;
      if (this.$route.name === 'projects') return this.projectscolor;
      if (this.$route.params.option === 'important') return this.importantcolor;
      if (this.$route.params.option === 'not-to-do') return this.nottodocolor;
      if (this.$route.params.option === 'habits') return this.habitscolor;
      return 'white';
    },
    hideNav() {
      if (this.$route.name === 'onboarding') return !this.showNav;
      return this.showNav;
    },
  },
  methods: {
    // edits todo when an id could be found
    handleEditTodo(id) {
      this.todoBeingEdited = this.$store.state.loadedTodos.find((todo) => todo.id === id);
      this.showModal = true;
    },
    // adds new todo to database and loaded todos
    async addTodo(todo) {
      const projectName = todo.newTodoProject;
      const motiveText = todo.newTodoMotive;
      let projectId; // the content is dependend on different conditions that's why it's only declared
      let motiveId;

      // try {
      // if the project doesn't have a property 'projectName'
      if (projectName) {
        // check if there's this project in the database
        const project = await db.projects.where('name').equalsIgnoreCase(projectName).first();
        if (project) projectId = project.id;
        else {
          const newProject = { name: projectName };
          // if not create a new one with the inputted name. this.addProject returns the id of the newly created project, so we can assign it right away
          projectId = await this.$store.dispatch('addProject', newProject);
        }
      }
      if (motiveText) {
        const motive = await db.motives.where('text').equalsIgnoreCase(motiveText).first();
        if (motive) motiveId = motive.id;
        else {
          const newMotive = { text: motiveText };
          motiveId = await this.$store.dispatch('addMotive', newMotive);
        }
      }
      // create new todo
      const newTodo = {
        text: todo.newTodo,
        done: false,
        project: projectId,
        motive: motiveId,
        timeExpected: todo.timeExpected || 0.25, // sets value to 0.25 if there's no value defined
        timeNeeded: todo.timeNeeded || 0,
        dueDate: todo.dueDate,
        category: todo.category,
      };
      await this.$store.dispatch('addTodo', newTodo); // adds todo to database and loaded todos
      this.closeTodoModal();
    },
    closeTodoModal() {
      this.todoBeingEdited = null; // sets todoBeingEdited to null after closing the Modal
      this.showModal = false;
    },
    // this function changes an existing todo in the database and the loaded todos with the changes that are passed in changes
    async updateTodo(todoId, changes) {
      try {
        if (this.todoBeingEdited) { // if a todo is chaned
          if (changes.newTodoProject) { // and if there' the property newTodoProject
            // it checks if there's a project with this name
            let project = await db.projects.where('name').equalsIgnoreCase(changes.newTodoProject).first();
            // if not one is added
            if (!project) project = await this.$store.dispatch('addProject', { name: changes.newTodoProject }); // this.addProject(changes.newTodoProject);
            else project = project.id; // otherwise only the id is saved
            // if the project of the todo we need to change isn't the project-id, the changes are added to 'changes'
            // if the todo originally had a project, but now no project name was passed, it means that the todo no longer has a project and the reference to a project id is removed
            if (this.todoBeingEdited.project !== project) changes.project = project; // eslint-disable-line no-param-reassign
            // in any case changes.projectName is removed, because there's no property with this name in the todo objects (they only store the id of a project, not its name)
            delete changes.newTodoProject; // eslint-disable-line no-param-reassign
          } else if (this.todoBeingEdited.project) changes.project = null; // eslint-disable-line no-param-reassign

          if (changes.newTodoMotive) {
            let motive = await db.motives.where('text').equalsIgnoreCase(changes.newTodoMotive).first();
            if (!motive) motive = await this.$store.dispatch('addMotive', { text: changes.newTodoMotive });// this.addMotive(changes.newTodoMotive);
            else motive = motive.id;

            if (this.todoBeingEdited.motive !== motive) changes.motive = motive; // eslint-disable-line no-param-reassign
            delete changes.newTodoMotive; // eslint-disable-line no-param-reassign
          } else if (this.todoBeingEdited.motive) changes.motive = null; // eslint-disable-line no-param-reassign
        }
        await this.$store.dispatch('updateTodo', { todoId, changes }); // update the todo with the new id and the loaded todo
        this.closeTodoModal();
      } catch (err) {
        this.$store.commit('addToast', { message: `Sorry. Your rhythm could not be updated: ${err.message}` });
      }
    },
  },
  watch: {
    //  react to switching between certain routes
    $route(to, from) {
      // Get prompt if it exits AND when change from settings back to home -> Ask user to install the app only after onboarding
      if (to.name === 'projects' && from.name === 'home' && this.$store.state.installPrompt) {
        this.$store.commit('addToast', {
          action: async () => {
            // open installation dialogue
            this.$store.state.installPrompt.prompt();
            // answer of the user in userChoice
            const choice = await this.$store.state.installPrompt.userChoice;

            if (choice.outcome === 'accepted') {
              this.$store.commit('addToast', {
                message: 'Rhythm should have been added to your homescreen or desktop. You can now launch it from there and close this tab.',
              });
            } else {
              this.$store.commit('addToast', { message: 'Okay, if you want to install rhythm later you can download it in your settings.' });
              // it would be better here to delete or save the prompt. To know that the user does not want to download the app
            }
          },
          actionLabel: 'yes',
          message: 'Do you want to install rhythm?',
          timeout: -1,
        });
      }
    },
  },
  props: {
    actionLabel: String,
  },
};
</script>

<style lang="stylus">

@require '/assets/styles/breakpoints'

#app
  .bar
    position: fixed
    top: 0
    width: 100%

  .toaster
    position: fixed
    bottom: 0px
    z-index: 20
    left:0
    right:0

    .toast
      border-top: 0.5px solid white
      background-color: black
      color: white
      border-radius: 1px
      display: flex
      align-items: center
      justify-content: space-between
      margin-bottom: 1px
      font-family: futura-pt, sans-serif
      font-weight: 400;
      font-style: normal
      font-size: 13px
      height: 120px
      text-transform: uppercase
      >>> .icon
        width: 15px
        height: 15px

      &.v-enter-active,
      &.v-leave-active,
      &.v-move
        transition: transform 200ms ease;

        &.v-enter,
        &.v-leave-to
          translate: transformY(100%)

      &.v-leave-active
        position: absolute
        width: 100%
      //actionlabel
      button
        background-color: black
        color: white
        outline: none
        border: none
        font-family: futura-pt, sans-serif
        font-weight: 400;
        font-style: normal
        font-size: 13px
        text-transform: uppercase
        font-family: futura-pt, sans-serif
        font-weight: 400;
        font-style: normal
        font-size: 16px
        right: 0
        position: absolute
        margin-right: 90px
        padding: 6px
        border-top: 0.5px solid white
        border-bottom: 0.5px solid white

        &:active
          border: 0.5px solid white

        &:hover
          border: 0.5px solid white

        &:focus
          border: 0.5px solid white

        @media $desktop
          margin-right: 90px

      p
        font-family: futura-pt, sans-serif
        font-weight: 400;
        font-style: normal
        font-size: 13px
        margin-left: 40px
        text-transform: uppercase

        @media $desktop
          margin-left: 90px


</style>
